import { describeSchedule, REPORT_LANGUAGE_NAMES, type ReportSchedule } from '@hoopoe/shared';
import { createFileRoute, Link } from '@tanstack/react-router';
import { BellRing, FilePlus2, LayoutTemplate, Lock, ScrollText, Users, Wrench } from 'lucide-react';
import { DataNumber, DataText } from '../components/data/data-text';
import { AllFindings } from '../components/findings/all-findings';
import { EmptyState } from '../components/guidance/empty-state';
import { ErrorState } from '../components/guidance/error-state';
import { Tip } from '../components/guidance/tip';
import { useScreenTour } from '../components/guidance/tour';
import { PageHeader } from '../components/layout/app-shell';
import { AdvancedToggle } from '../components/reports/advanced-toggle';
import { Button } from '../components/ui/button';
import { useAdvancedReports, useMySchedules, useReports, useReportTemplates } from '../lib/reports';
import { useMe } from '../lib/session';

// Reports (PLAN §12.7 screen 8; task 31; Report v2): saved reports (mine and shared ones) with their owner, whether
// they are private or shared, their language and when they changed. "New report" describes a report in words
// (/reports/new). The Advanced toggle (remembered per person) adds the question builder and the templates a report can
// start from.

export const Route = createFileRoute('/reports/')({
  // `?advanced=1` shows the Advanced tools for this view without changing the person's preference.
  validateSearch: (s: Record<string, unknown>): { advanced?: boolean } =>
    s.advanced === true || s.advanced === 1 || s.advanced === '1' ? { advanced: true } : {},
  component: ReportsPage,
});

const TOUR = {
  id: 'reports',
  steps: [
    {
      target: '[data-tour="new-report"]',
      title: 'Describe a report',
      body: 'Say what the report should cover; the AI plans it, you check the plan, then it is written section by section.',
      learnMore: 'build-a-report',
    },
    {
      target: '[data-tour="advanced"]',
      title: 'Advanced',
      body: 'Turn it on for the question builder, the templates and the tools behind a report.',
      learnMore: 'build-a-report',
    },
  ],
};

const dateOf = (ms: number) => new Date(ms).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });

function ReportsPage() {
  useScreenTour(TOUR);
  const reports = useReports();
  const { advanced: advancedPref } = useAdvancedReports();
  const { advanced: advancedParam } = Route.useSearch();
  const advanced = advancedPref || !!advancedParam;
  const templates = useReportTemplates();
  const { data: me } = useMe();
  const schedules = useMySchedules();
  const scheduled = new Map((schedules.data?.schedules ?? []).map(s => [s.reportId, s]));
  const list = reports.data?.reports ?? [];
  return (
    <>
      <PageHeader
        title="Reports"
        description="Describe the report you need; read and edit it as a document; share it and export it."
        actions={
          <div className="flex flex-wrap items-center gap-3">
            <div data-tour="advanced" className="rounded-md border bg-surface px-3 py-1.5">
              <AdvancedToggle />
            </div>
            {advanced && (
              <Button size="sm" variant="outline" asChild>
                <Link to="/reports/new" search={{ advanced: true }}>
                  <Wrench aria-hidden="true" /> Build a question
                </Link>
              </Button>
            )}
            <Button size="sm" asChild data-tour="new-report">
              <Link to="/reports/new">
                <FilePlus2 aria-hidden="true" /> New report
              </Link>
            </Button>
          </div>
        }
      />
      <Tip id="reports" learnMore="build-a-report">
        A report is a saved question answered on the data as it is when you open it, written up as a document you edit. Off-record claims
        are left out of reports and their exports.
      </Tip>
      <section aria-labelledby="saved-reports" className="mt-6 space-y-3">
        <h2 id="saved-reports" className="text-lg font-semibold">
          Saved reports
        </h2>
        {reports.isError ? (
          <ErrorState error={reports.error} title="Reports couldn’t be loaded" onRetry={() => reports.refetch()} compact />
        ) : reports.isPending ? (
          <div className="h-24 animate-pulse rounded-md bg-muted" aria-busy="true">
            <span className="sr-only">Loading…</span>
          </div>
        ) : list.length === 0 ? (
          <EmptyState
            icon={ScrollText}
            title="No saved reports yet"
            learnMore="build-a-report"
            action={
              <Button size="sm" asChild>
                <Link to="/reports/new">
                  <FilePlus2 aria-hidden="true" /> New report
                </Link>
              </Button>
            }
          >
            Describe the report you need and it is planned, written and illustrated for you. Shared reports from your colleagues appear here
            too.
          </EmptyState>
        ) : (
          <div className="relative overflow-x-auto rounded-lg border bg-surface">
            <table className="w-full text-sm" data-testid="reports-table">
              <caption className="sr-only">Saved reports</caption>
              <thead>
                <tr className="border-b bg-muted text-xs">
                  <th scope="col" className="p-2 text-start font-medium">
                    Name
                  </th>
                  <th scope="col" className="p-2 text-start font-medium">
                    Owner
                  </th>
                  <th scope="col" className="p-2 text-start font-medium">
                    Sharing
                  </th>
                  <th scope="col" className="p-2 text-start font-medium">
                    Language
                  </th>
                  <th scope="col" className="p-2 text-start font-medium">
                    Updated
                  </th>
                </tr>
              </thead>
              <tbody>
                {list.map(r => (
                  <tr key={r.id} className="border-b align-top last:border-0">
                    <td className="p-2">
                      <Link to="/reports/$id" params={{ id: r.id }} className="font-medium hover:underline">
                        <DataText>{r.name}</DataText>
                      </Link>
                      {scheduled.has(r.id) && (
                        <span
                          className="ms-2 inline-flex items-center gap-1 rounded-full border px-1.5 text-xs text-muted-foreground"
                          title={describeSchedule(scheduled.get(r.id) as ReportSchedule)}
                        >
                          <BellRing className="size-3" aria-hidden="true" /> {scheduled.get(r.id)?.paused ? 'Schedule paused' : 'Scheduled'}
                        </span>
                      )}
                      {r.summary && (
                        <span className="block max-w-xl text-xs text-muted-foreground">
                          <DataText>{r.summary}</DataText>
                        </span>
                      )}
                    </td>
                    <td className="p-2 text-muted-foreground">
                      {r.createdBy && r.createdBy === me?.id ? 'You' : <DataText>{r.ownerName ?? '—'}</DataText>}
                    </td>
                    <td className="p-2 text-muted-foreground">
                      <span className="inline-flex items-center gap-1">
                        {r.shared ? <Users className="size-3" aria-hidden="true" /> : <Lock className="size-3" aria-hidden="true" />}
                        {r.shared ? 'Shared' : 'Private'}
                      </span>
                    </td>
                    <td className="p-2 text-muted-foreground">{REPORT_LANGUAGE_NAMES[r.language] ?? r.language}</td>
                    <td className="p-2 text-muted-foreground">
                      <DataNumber>{dateOf(r.updatedAt)}</DataNumber>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
      <AllFindings />
      {advanced && (
        <section aria-labelledby="report-templates" className="mt-8 space-y-3" data-tour="templates">
          <div className="flex items-center gap-2">
            <LayoutTemplate className="size-4 text-muted-foreground" aria-hidden="true" />
            <h2 id="report-templates" className="text-lg font-semibold">
              Start from a template
            </h2>
          </div>
          <p className="text-sm text-muted-foreground">
            Templates give a report its sections. A described report picks one on its own; here you build the question by hand.
          </p>
          {templates.isError ? (
            <ErrorState error={templates.error} title="Templates couldn’t be loaded" onRetry={() => templates.refetch()} compact />
          ) : (
            <ul className="grid gap-3 md:grid-cols-2">
              {(templates.data?.templates ?? []).map(t => (
                <li key={t.id} className="space-y-2 rounded-lg border bg-surface p-4">
                  <div className="flex items-start justify-between gap-3">
                    <h3 className="font-medium">{t.name}</h3>
                    <Button size="sm" variant="outline" asChild>
                      <Link to="/reports/new" search={{ template: t.id, advanced: true }} aria-label={`Use template ${t.name}`}>
                        Use
                      </Link>
                    </Button>
                  </div>
                  {t.description && <p className="text-sm text-muted-foreground">{t.description}</p>}
                  <ol className="list-decimal space-y-0.5 ps-5 text-sm">
                    {t.sections.map(s => (
                      <li key={s.key}>{s.title}</li>
                    ))}
                  </ol>
                </li>
              ))}
            </ul>
          )}
        </section>
      )}
    </>
  );
}
